import { Rocket, Building2, Wrench, RefreshCw } from "lucide-react";
import React from "react";

export interface Scenario {
  id: string;
  name: string;
  icon: React.ReactNode;
  budgetAvailable: number;
  timeAvailable: number;
  technicalComplexity: number;
  requirementsClarity?: number;
  changeReadiness?: number;
  company: {
    name: string;
    industry: string;
    size: string;
    revenue: string;
    founded: string;
    description: string;
  };
  decisionMaker: {
    name: string;
    role: string;
    background: string;
  };
  story: {
    situation: string;
    thoughtProcess: string;
    whatTheyDecided: string;
    assessingCorrectly: string[];
    potentiallyMissing: string[];
    concerns: string[];
    whatTheyDid: string;
    outcome: string;
  };
}

export const scenarios: Scenario[] = [
  {
    id: "startup-mvp",
    name: "Startup MVP",
    icon: <Rocket className="w-5 h-5" />,
    budgetAvailable: 75,
    timeAvailable: 70,
    technicalComplexity: 40,
    company: {
      name: "TechStart",
      industry: "SaaS - Workflow Automation",
      size: "6 employees",
      revenue: "Pre-revenue (bootstrapped)",
      founded: "2024",
      description:
        "Early-stage startup building workflow automation software for small businesses. Currently bootstrapped with seed funding from founders and angel investors.",
    },
    decisionMaker: {
      name: "Sarah Chen",
      role: "Co-Founder & CEO",
      background:
        "Former product manager at a mid-size SaaS company. 8 years experience in B2B software. First-time founder.",
    },
    story: {
      situation:
        "Sarah's team has 6 months of runway remaining. They've raised $80K from angels and need to launch an MVP to prove the concept before their next fundraising round. The product is a workflow automation tool that integrates with Slack.",
      thoughtProcess:
        "Sarah knows from her PM experience that rushed products fail. She's seen too many startups blow their budget on agencies that deliver unusable code. At the same time, she can't afford to over-engineer—they need to validate the core hypothesis quickly. The 6-month timeline feels tight but achievable if they scope carefully.",
      whatTheyDecided:
        "Allocate $50K to development (leaving $30K for operations and marketing). Hire one senior full-stack developer at $120/hr for 3 months of focused work. Sarah will handle product management and design. Timeline: 3 months to build, 2 months to test with beta users, 1 month buffer.",
      assessingCorrectly: [
        "Hiring senior talent—quality matters more than quantity for MVPs",
        "Leaving runway for marketing and operations—build it and they won't come without promotion",
        "Building in a buffer—software always takes longer than planned",
        "Keeping scope tight—one integration, core workflow only",
      ],
      potentiallyMissing: [
        "Technical debt repayment time—that 3-month build will need refactoring",
        "Security and compliance requirements if they land enterprise customers",
        "Infrastructure costs as they scale beta users",
        "The integration might be harder than expected—APIs change, docs are incomplete",
      ],
      concerns: [
        "What if 3 months isn't enough? They can't extend the timeline without more funding",
        "What if the developer leaves mid-project? No knowledge transfer plan",
        "What if beta users want features they didn't plan for?",
        "What if investors want more polish before they'll fund the next round?",
      ],
      whatTheyDid:
        "Sarah hired a senior dev, kept daily standups, and ruthlessly cut scope. They launched in 4 months with core features only. Beta users loved it but requested 8 additional features. Sarah resisted feature creep and focused on proving the core value prop.",
      outcome:
        "MVP launched successfully. 50 beta users, 15 paying customers at $50/month. Raised $500K seed round. The technical debt is real—they'll need to refactor before scaling—but they proved the concept and bought themselves 18 months of runway.",
    },
  },
  {
    id: "enterprise",
    name: "Enterprise System",
    icon: <Building2 className="w-5 h-5" />,
    budgetAvailable: 25,
    timeAvailable: 40,
    technicalComplexity: 80,
    company: {
      name: "MegaCorp Manufacturing",
      industry: "Industrial Manufacturing",
      size: "500 employees across 3 facilities",
      revenue: "$85M annually",
      founded: "1987",
      description:
        "Mid-market manufacturer of industrial components. Running on a patchwork of 12 legacy systems accumulated over 30+ years. Recent private equity acquisition pushing for operational efficiency.",
    },
    decisionMaker: {
      name: "David Martinez",
      role: "CFO",
      background:
        "Former Big 4 consultant, CFO for 3 years. Brought in by PE firm to 'modernize operations.' No technical background but lots of pressure to show ROI.",
    },
    story: {
      situation:
        "The PE firm wants operational efficiency metrics before their exit in 18 months. David convinced the board to approve $2M for ERP consolidation. The CIO warned it's not enough, but the board said 'make it work.' IT is already underwater maintaining legacy systems.",
      thoughtProcess:
        "David thinks: 'We spend $400K/year on maintenance for these old systems. A one-time $2M investment should pay for itself in 5 years. How hard can it be to consolidate some databases?' He's seen consulting projects come in on-time and on-budget before. He doesn't understand why IT is pushing back so hard.",
      whatTheyDecided:
        "$2M budget, 12-month timeline. Hire a large consulting firm to 'own the delivery.' IT will provide requirements and testing. Go-live in 12 months, no excuses. If IT can't handle it, we'll find people who can.",
      assessingCorrectly: [
        "The current system maintenance cost is real and painful",
        "Consolidation will eventually provide value",
        "Need external expertise—internal IT is stretched thin",
      ],
      potentiallyMissing: [
        "Enterprise ERP projects typically cost $5-15M for this complexity level",
        "12-month timeline is 50% of realistic timeframe",
        "Each legacy system has undocumented business logic that took years to build",
        "Data migration alone could take 6+ months",
        "Can't shut down operations during migration—need parallel systems",
        "Change management and training for 500 employees",
        "Integration with external suppliers and customers",
      ],
      concerns: [
        "Board will blame him if this fails",
        "PE firm expects results—his job depends on showing ROI",
        "What if IT sabotages the project because they're upset?",
        "Competitors are more efficient—we're losing bids because our quoting process takes 3 days",
      ],
      whatTheyDid:
        "Hired large consulting firm for $1.8M. Project kicked off with fanfare. 3 months in, consultants discovered 4 'undocumented' legacy systems. 6 months in, budget is blown, go-live pushed to 18 months. IT is working 60-hour weeks fixing data migration issues.",
      outcome:
        "Project failed after 14 months and $4.2M spent. Partially migrated—now running BOTH old and new systems. PE firm postponed exit. David left for 'other opportunities.' New CTO brought in to salvage the mess. Will take another $2M and 12 months to finish properly.",
    },
  },
  {
    id: "internal-tool",
    name: "Internal Tool",
    icon: <Wrench className="w-5 h-5" />,
    budgetAvailable: 60,
    timeAvailable: 50,
    technicalComplexity: 50,
    company: {
      name: "GrowthCo",
      industry: "E-commerce - Home Goods",
      size: "50 employees (12 in operations)",
      revenue: "$8M annually, growing 40% YoY",
      founded: "2019",
      description:
        "Fast-growing direct-to-consumer e-commerce company selling home goods. Bootstrapped to profitability. Strong product-market fit but operationally chaotic due to rapid growth.",
    },
    decisionMaker: {
      name: "Jennifer Park",
      role: "COO",
      background:
        "Former operations director at a logistics company. Joined 18 months ago to 'bring order to chaos.' Understands both business ops and basic tech.",
    },
    story: {
      situation:
        "Operations team manually reconciles inventory across Shopify, QuickBooks, and their 3PL warehouse system. Takes 20 hours/week, error-prone, causing stockouts and overselling. CEO allocated $120K to 'fix the inventory problem' after losing a major wholesale deal due to inaccurate stock levels.",
      thoughtProcess:
        "Jennifer has done this before. She knows manual processes don't scale. The team is already working overtime—hiring more people just adds coordination overhead. An automated dashboard pulling data from all three systems could save 1,000 hours/year and eliminate errors. That's a no-brainer ROI. But she's also seen 'simple' integration projects balloon when APIs don't work as documented or business logic gets complicated.",
      whatTheyDecided:
        "$100K for development, $20K contingency. 4-5 month timeline—aggressive but not insane. Hire boutique dev shop specializing in e-commerce integrations. Keep it simple: read-only dashboard first, no automated actions yet. Jennifer will manage the project personally with weekly check-ins.",
      assessingCorrectly: [
        "ROI is clear—quantified time savings and error reduction",
        "Phased approach—dashboard before automation reduces risk",
        "Specialist shop has e-commerce experience—not learning on her dime",
        "Personal involvement—she'll catch scope creep early",
        "Keeping it internal-only—no customer-facing features to complicate it",
      ],
      potentiallyMissing: [
        "API rate limits—Shopify has strict throttling that could require caching strategy",
        "Real-time vs batch updates—warehouse system might not have webhooks",
        "Historical data—might need to backfill months of data for trending",
        "User adoption—ops team might resist new tools if not involved in design",
        "Long-term maintenance—who maintains this when the shop is done?",
      ],
      concerns: [
        "What if one of the APIs changes mid-project?",
        "What if the warehouse system doesn't have the data granularity we need?",
        "What if it works but the team doesn't use it?",
        "What if this becomes a 'just one more feature' project that never ends?",
      ],
      whatTheyDid:
        "Hired dev shop with strong references. Kicked off with 2-week discovery to validate API capabilities. Built iteratively with ops team testing weekly. Launched v1 in 4.5 months: dashboard showing real-time inventory across all systems, basic alerts for stockouts.",
      outcome:
        "Success! Ops team uses it daily. Reduced reconciliation time from 20 hours to 2 hours/week. Caught a major API change early because of weekly testing. Now planning v2 with automated reorder suggestions. Total cost: $98K. Jennifer's hero project—CEO now asks her to 'automate everything.'",
    },
  },
  {
    id: "migration",
    name: "Legacy Migration",
    icon: <RefreshCw className="w-5 h-5" />,
    budgetAvailable: 40,
    timeAvailable: 30,
    technicalComplexity: 75,
    company: {
      name: "OldGuard Logistics",
      industry: "Freight & Logistics",
      size: "120 employees across 8 locations",
      revenue: "$22M annually (declining)",
      founded: "1988",
      description:
        "Regional freight broker running on legacy AS/400 mainframe system from 1995. System handles customer orders, carrier dispatch, invoicing, and commission calculations. Business is steady but losing market share to tech-enabled competitors.",
    },
    decisionMaker: {
      name: "Robert 'Bob' Thompson",
      role: "VP of IT",
      background:
        "Been with the company for 32 years. Built the original AS/400 system in his 30s. Now 62, planning to retire in 8 months. The only person who truly understands the system's 30 years of accumulated business logic.",
    },
    story: {
      situation:
        "Bob announced his retirement 2 months ago. The CEO suddenly realized: nobody else knows how the core system works. It's undocumented, written in RPG, and contains business logic for customer-specific pricing rules, carrier contracts, and commission structures that evolved over decades. The business can't function without it. Board approved $400K emergency budget—all they can spare without jeopardizing the business.",
      thoughtProcess:
        "Bob feels guilty. He knows he should have documented things, should have mentored someone, should have pushed for modernization years ago. But there was always a fire to fight, always another urgent request. He wants to retire, but he doesn't want to leave the company in a crisis. $400K seems like a lot, but he knows from experience this migration is easily a $1-2M project. The 8-month timeline is impossible—but what choice do they have?",
      whatTheyDecided:
        "Spend 2 months documenting the system with Bob. Use remaining $300K to hire consultants to build a modern replacement in 6 months. Bob will delay retirement by 3 months to help with transition. Work nights and weekends to meet the deadline. It's their only chance.",
      assessingCorrectly: [
        "Bob's knowledge is critical—documentation must be priority #1",
        "Can't replace everything at once—need phased migration",
        "This is genuinely urgent—knowledge loss is catastrophic",
        "Need external help—nobody internal has modern development skills",
      ],
      potentiallyMissing: [
        "2 months of documentation won't capture 30 years of edge cases",
        "$300K can maybe build 40% of what the current system does",
        "Testing will reveal the real complexity—untested migrations always fail",
        "Customers will notice changes to how invoicing works—need change management",
        "Carrier integrations might not have modern APIs available",
        "Commission calculations have exceptions Bob doesn't consciously remember",
        "Bob burning out in the next 8 months is a real risk",
      ],
      concerns: [
        "What if Bob has a health issue? Knowledge lost forever",
        "What if consultants can't deliver? Company shuts down",
        "What if the budget runs out halfway through?",
        "What if we go live and realize critical features are missing?",
        "What if customers leave during the transition?",
        "What if this destroys my legacy at the company?",
      ],
      whatTheyDid:
        "Documentation started but overwhelmed by daily support. Hired cheap offshore firm to save money. Built 60% of features in 5 months. Went live early because Bob couldn't delay retirement any longer. System missing key features. Customers complaining. Commissions wrong.",
      outcome:
        "Partial failure. New system launched but incomplete. Running parallel systems—some customers on old, some on new. Bob stayed extra 6 months (guilt). Hired another firm for $600K to finish properly. 18 months later, finally fully migrated. Total cost: $1.3M. Business survived but bruised. Lost 3 major customers during transition.",
    },
  },
];
